import {
    Button,
    Checkbox,
    Col,
    Form,
    Input,
    InputNumber,
    Row,
    Select,
    Typography,
    message,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useApi } from '../contexts/ApiContext';
import type { CashPointAlertSettingsPayload } from '../domain/cashPointAlertSettings';
import useRequest from '../hooks/useRequest';

const { Text, Title } = Typography;

/**
 * The Modules tab's till alert and overpaid tolerances, edited for one cash point at a time.
 *
 * Delphi keeps these per cash point (`casa_alert_active`, `Casa_EMail`, `Casa_Min`, `casa_Max`,
 * `contract_supraplatit`, `contract_supraplatit_1zi`) and the limits differ between counters, so
 * they cannot share the tab's shop-wide form: its batch save addresses a setting by name alone.
 */
export default function CashPointAlertSettings() {
    const { t } = useTranslation();
    const { configuration } = useApi();
    const request = useRequest();
    const [form] = Form.useForm<CashPointAlertSettingsPayload>();

    const [cashPoints, setCashPoints] = useState<{ id: number; name: string }[]>([]);
    const [cashPointId, setCashPointId] = useState<number | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        request<{ id: number; name: string }[]>({ path: '/cash-drawer/cashpoints' })
            .then(rows => {
                setCashPoints(rows);
                setCashPointId(current => current ?? rows[0]?.id ?? null);
            })
            .catch(() => message.error(t('configuration.modules.cashPointAlertsLoadError')));
    }, [request, t]);

    const load = useCallback(
        async (id: number) => {
            const response = await configuration.getCashPointAlerts(id);
            if (!response.success || response.data === undefined) {
                message.error(
                    response.error ?? t('configuration.modules.cashPointAlertsLoadError'),
                );
                return;
            }
            form.setFieldsValue(response.data);
        },
        [configuration, form, t],
    );

    useEffect(() => {
        if (cashPointId !== null) {
            void load(cashPointId);
        }
    }, [cashPointId, load]);

    const save = async (values: CashPointAlertSettingsPayload) => {
        if (cashPointId === null) {
            return;
        }
        setSaving(true);
        try {
            const response = await configuration.saveCashPointAlerts(cashPointId, {
                tillAlertEnabled: values.tillAlertEnabled === true,
                tillAlertEmail: values.tillAlertEmail ?? '',
                tillMinimum: values.tillMinimum ?? 0,
                tillMaximum: values.tillMaximum ?? 0,
                overpaidTolerancePercent: values.overpaidTolerancePercent ?? 0,
                overpaidSingleDayTolerancePercent: values.overpaidSingleDayTolerancePercent ?? 0,
            });
            if (!response.success || response.data === undefined) {
                message.error(
                    response.error ?? t('configuration.modules.cashPointAlertsSaveError'),
                );
                return;
            }
            form.setFieldsValue(response.data);
            message.success(t('configuration.saveSuccess'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={values => void save(values)}
            data-testid="cash-point-alerts-form"
        >
            <Title level={5}>{t('configuration.modules.cashAlert')}</Title>
            <Text type="secondary">{t('configuration.modules.cashAlertHint')}</Text>
            <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={8}>
                    <Form.Item label={t('configuration.modules.cashPoint')}>
                        <Select
                            value={cashPointId ?? undefined}
                            onChange={setCashPointId}
                            data-testid="cash-point-alerts-cash-point"
                            options={cashPoints.map(cashPoint => ({
                                value: cashPoint.id,
                                label: `${cashPoint.id} — ${cashPoint.name}`,
                            }))}
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={8}>
                    <Form.Item name="tillMinimum" label={t('configuration.modules.minCash')}>
                        <InputNumber style={{ width: '100%' }} min={0} precision={0} />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item name="tillMaximum" label={t('configuration.modules.maxCash')}>
                        <InputNumber style={{ width: '100%' }} min={0} precision={0} />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item name="tillAlertEmail" label={t('configuration.modules.email')}>
                        <Input type="email" placeholder={t('configuration.modules.enterEmail')} />
                    </Form.Item>
                </Col>
            </Row>
            <Form.Item name="tillAlertEnabled" valuePropName="checked">
                <Checkbox>{t('configuration.modules.active')}</Checkbox>
            </Form.Item>
            <Text strong>{t('configuration.modules.overpaidCashPoint')}</Text>
            <Row gutter={16} style={{ marginTop: 8 }}>
                <Col span={8}>
                    <Form.Item
                        name="overpaidTolerancePercent"
                        label={t('configuration.modules.overpaidAmount')}
                    >
                        <InputNumber style={{ width: '100%' }} min={0} precision={0} />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item
                        name="overpaidSingleDayTolerancePercent"
                        label={t('configuration.modules.overpaidPerDay')}
                    >
                        <InputNumber style={{ width: '100%' }} min={0} precision={0} />
                    </Form.Item>
                </Col>
            </Row>
            <Form.Item>
                <Button
                    type="primary"
                    htmlType="submit"
                    loading={saving}
                    disabled={cashPointId === null}
                    data-testid="save-cash-point-alerts"
                >
                    {t('configuration.save')}
                </Button>
            </Form.Item>
        </Form>
    );
}
